import React, { useCallback, useEffect, useState } from 'react';

import { rootUiActions } from 'app-components/flux/root-ui';

import StandardDrawer from 'components/Drawers/StandardDrawer';
import type {
  RootModalDrawerOptions,
  StandardDrawerPlane,
  StandardDrawerVariant,
} from 'components/Drawers/standardDrawerContract';
import { useAppDispatch } from 'hooks/redux';

import ModalRenderer from './ModalRenderer';
import type { RootModalRecord } from './types';

type RootModalDrawerRendererProps = {
  drawerOptions: RootModalDrawerOptions;
  modal: RootModalRecord;
  plane?: StandardDrawerPlane;
  variant: StandardDrawerVariant;
};

function RootModalDrawerRenderer({ drawerOptions, modal, plane, variant }: RootModalDrawerRendererProps) {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState<boolean>(modal.visible);
  const [isDirty, setIsDirty] = useState<boolean>(Boolean(drawerOptions.isDirty));
  useEffect(() => {
    setOpen(modal.visible);
  }, [modal.visible]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Root UI removal is delayed until the drawer exit finishes so MUI keeps the
  // child mounted long enough for close animations to run.
  const handleExited = useCallback(() => {
    dispatch(rootUiActions.removeDialog(modal.id));
  }, [dispatch, modal.id]);

  return (
    <StandardDrawer
      closeTransition={drawerOptions.closeTransition}
      dirtyConfirmation={drawerOptions.dirtyConfirmation}
      isDirty={isDirty}
      onClose={handleClose}
      onExited={handleExited}
      open={open}
      plane={plane}
      showScrim={drawerOptions.showScrim}
      variant={variant}
      width={drawerOptions.width}
    >
      <ModalRenderer
        modal={modal}
        onRootModalDrawerDirtyChange={setIsDirty}
        dialogType={modal.type}
        onRootModalDrawerClose={handleClose}
        rootModalDrawer={drawerOptions}
      />
    </StandardDrawer>
  );
}

export default React.memo(RootModalDrawerRenderer);
